import React, { useEffect, useState } from 'react';
import { Dialog, DialogTitle, DialogContent } from '@mui/material';
import { z } from 'zod';
import { FaWeight } from 'react-icons/fa'
import { useForm } from 'react-hook-form';
import { IoIosCloseCircle } from "react-icons/io";
import { zodResolver } from '@hookform/resolvers/zod';
import { useMyContext } from '../../../context/MyContext';
import '../../css/custom-scrollbar.css';
import { FaEye } from "react-icons/fa";
import { FaIdCard, FaAddressCard, FaTimes, FaEdit, FaSave } from 'react-icons/fa';
import { Url_For_View_Document } from '../../../config/config';

const schema = z.object({
  id: z.string().min(1, "ID is required"),
  seller_id: z.string().min(1, "Seller ID is required"),
  waybill: z.string().min(1, "Waybill is required"),
  initial_weight: z.string().min(1, "Initial Weight is required"),
  dimension: z.string().min(1, "Dimension is required"),
  product_des: z.string().min(1, "Product Description is required"),
  sku: z.string().min(1, "SKU is required"),
  date: z.string().min(1, "Date is required"),
  reversed_weight: z.string().min(1, "Reversed Weight is required"),
  price: z.string().min(1, "Price is required"),
  status: z.string().min(1, "Status is required"),
  timestamp: z.string().min(1, "Timestamp is required"),
});

const ViewWeightDisputes = ({ isopen, onClose, allWeightDisputes }) => {
  const { isDarkMode } = useMyContext();
  const [imageUrls, setImageUrls] = useState([]);
  const [isEditMode, setIsEditMode] = useState(false);
  const { register, handleSubmit, formState: { errors }, reset } = useForm({
    resolver: zodResolver(schema),
  });

  useEffect(() => {
    if (allWeightDisputes) {
      reset({
        id: allWeightDisputes.id || '',
        seller_id: allWeightDisputes.seller_id || '',
        waybill: allWeightDisputes.waybill || '',
        initial_weight: allWeightDisputes.initial_weight || '',
        dimension: allWeightDisputes.dimension || '',
        product_des: allWeightDisputes.product_des || '',
        sku: allWeightDisputes.sku || '',
        date: allWeightDisputes.date || '',
        reversed_weight: allWeightDisputes.reversed_weight || '',
        price: allWeightDisputes.price || '',
        status: allWeightDisputes.status || '',
        timestamp: allWeightDisputes.timestamp || '',
      });

      // Safe JSON parsing with error handling
      let images = [];

      if (allWeightDisputes.image) {
        try {
          const parsedImage = JSON.parse(allWeightDisputes.image);
          images = parsedImage.images || [];
        } catch (error) {

          // Optional: Try to extract partial data if JSON is truncated
          try {
            // Check if it's a truncated JSON string starting with images array
            const imageStr = allWeightDisputes.image;
            if (imageStr.includes('"images":[')) {
              // Extract the images array portion if possible
              const imagesStart = imageStr.indexOf('"images":[') + '"images":'.length;
              const imagesEnd = imageStr.lastIndexOf(']');

              if (imagesEnd > imagesStart) {
                const imagesArrayStr = imageStr.substring(imagesStart, imagesEnd + 1);
                images = JSON.parse(imagesArrayStr);
              }
            }
          } catch (fallbackError) {
            console.warn('Fallback parsing also failed:', fallbackError.message);
            images = []; // Set empty array as fallback
          }
        }
      }

      setImageUrls(images);
    }
  }, [allWeightDisputes, reset]);

  // Alternative approach: Create a utility function for safe JSON parsing
  const safeJsonParse = (jsonString, defaultValue = {}) => {
    if (!jsonString) return defaultValue;

    try {
      return JSON.parse(jsonString);
    } catch (error) {
      console.warn('JSON parsing failed:', error.message);
      return defaultValue;
    }
  };

  // Usage with the utility function:
  useEffect(() => {
    if (allWeightDisputes) {
      reset({
        id: allWeightDisputes.id || '',
        seller_id: allWeightDisputes.seller_id || '',
        waybill: allWeightDisputes.waybill || '',
        initial_weight: allWeightDisputes.initial_weight || '',
        dimension: allWeightDisputes.dimension || '',
        product_des: allWeightDisputes.product_des || '',
        sku: allWeightDisputes.sku || '',
        date: allWeightDisputes.date || '',
        reversed_weight: allWeightDisputes.reversed_weight || '',
        price: allWeightDisputes.price || '',
        status: allWeightDisputes.status || '',
        timestamp: allWeightDisputes.timestamp || '',
      });

      // Safe parsing using utility function
      const imageData = safeJsonParse(allWeightDisputes.image, { images: [] });
      const images = imageData.images || [];
      setImageUrls(images);
    }
  }, [allWeightDisputes, reset]);

  const onSubmit = (data) => {
    onClose();
  };

  const handleClose = () => {
    setIsEditMode(false);
    onClose();
  };

  const handleEyeClick = (url) => {
    const viewDocumentUrl = Url_For_View_Document + url;
    window.open(viewDocumentUrl, "_blank");
  }

  const handleEditToggle = () => {
    setIsEditMode((prev) => !prev);
  };

  // ✅ Shared compact field style with dark mode
  const inputClass = `mt-1 px-2.5 py-1.5 text-xs border rounded-md w-full transition-all outline-none
    border-gray-300 focus:ring-1 focus:ring-black focus:border-black
    disabled:bg-gray-50 disabled:text-gray-500
    dark:border-gray-700 dark:bg-[#1F2937] dark:text-white dark:disabled:bg-[#111827] dark:disabled:text-gray-500
    dark:focus:ring-indigo-500 dark:focus:border-indigo-500`;

  const labelClass = "block text-[11px] font-semibold text-gray-600 dark:text-gray-400 mb-1 uppercase tracking-wide";

  const sectionClass = `border rounded-lg p-3 transition-colors duration-300 ${
    isDarkMode 
      ? 'border-gray-800 bg-[#0F172A]/40' 
      : 'border-gray-200'
  }`;

  return (
    <Dialog
      open={isopen}
      onClose={handleClose}
      maxWidth="sm"
      fullWidth
      PaperProps={{
        className: `rounded-lg overflow-hidden transition-colors duration-300 ${
          isDarkMode ? 'dark bg-[#111827] border border-gray-800 shadow-[0_0_50px_rgba(0,0,0,0.7)]' : ''
        }`
      }}
    >
      {/* Header */}
      <DialogTitle className={`relative py-2.5 px-4 transition-colors duration-300 ${
        isDarkMode 
          ? 'bg-gradient-to-r from-indigo-600 via-purple-600 to-indigo-600 text-white' 
          : 'bg-black text-white'
      }`}>
        <h2 className="text-xs font-semibold flex items-center gap-2">
          <FaWeight size={13} />
          Weight Dispute Details
        </h2>
        <IoIosCloseCircle
          className="absolute top-2.5 right-3 cursor-pointer hover:opacity-70 transition-opacity"
          size={18}
          onClick={handleClose}
        />
      </DialogTitle>

      <DialogContent className={`custom-scrollbar p-0 transition-colors duration-300 ${isDarkMode ? 'bg-[#111827] text-gray-200' : 'bg-white dark:bg-black dark:text-white'}`} style={{ maxHeight: '75vh' }}>
        <div className="py-2">
          <form className="space-y-2" onSubmit={handleSubmit(onSubmit)}>

            {/* Primary Information */}
            <div className={sectionClass}>
              <h3 className={`text-xs font-semibold mb-2.5 flex items-center gap-1.5 ${isDarkMode ? 'text-indigo-300' : 'text-black dark:text-white'}`}>
                <FaIdCard size={11} />
                Primary Information
              </h3>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <div>
                  <label className={labelClass}>ID</label>
                  <input type="text" className={inputClass} {...register('id')} disabled={!isEditMode} />
                  {errors.id && <p className="text-red-500 text-[10px] mt-1">{errors.id.message}</p>}
                </div>

                <div>
                  <label className={labelClass}>Seller ID</label>
                  <input type="text" className={inputClass} {...register('seller_id')} disabled={!isEditMode} />
                  {errors.seller_id && <p className="text-red-500 text-[10px] mt-1">{errors.seller_id.message}</p>}
                </div>
              </div>
            </div>

            {/* Additional Information */}
            <div className={sectionClass}>
              <h3 className={`text-xs font-semibold mb-2.5 flex items-center gap-1.5 ${isDarkMode ? 'text-indigo-300' : 'text-black dark:text-white'}`}>
                <FaAddressCard size={11} />
                Additional Information
              </h3>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <div>
                  <label className={labelClass}>Waybill</label>
                  <input type="text" className={inputClass} {...register('waybill')} disabled={!isEditMode} />
                  {errors.waybill && <p className="text-red-500 text-[10px] mt-1">{errors.waybill.message}</p>}
                </div>

                <div>
                  <label className={labelClass}>Initial Weight</label>
                  <input type="text" className={inputClass} {...register('initial_weight')} disabled={!isEditMode} />
                  {errors.initial_weight && <p className="text-red-500 text-[10px] mt-1">{errors.initial_weight.message}</p>}
                </div>

                <div>
                  <label className={labelClass}>Dimension</label>
                  <input type="text" className={inputClass} {...register('dimension')} disabled={!isEditMode} />
                  {errors.dimension && <p className="text-red-500 text-[10px] mt-1">{errors.dimension.message}</p>}
                </div>

                <div>
                  <label className={labelClass}>Product Description</label>
                  <input type="text" className={inputClass} {...register('product_des')} disabled={!isEditMode} />
                  {errors.product_des && <p className="text-red-500 text-[10px] mt-1">{errors.product_des.message}</p>}
                </div>
              </div>
            </div>

            {/* Image Section */}
            {imageUrls.length > 0 && (
              <div className={sectionClass}>
                <label className={`text-[11px] font-semibold mb-1 uppercase tracking-wide block ${isDarkMode ? 'text-indigo-300' : 'text-gray-600 dark:text-gray-400'}`}>
                  Image For Dimension
                </label>
                <div className="space-y-1.5">
                  {imageUrls.map((url, index) => (
                    <div key={index} className="flex items-center gap-2">
                      <input
                        type="text"
                        value={url}
                        className={`${inputClass} mt-0`}
                        disabled
                      />
                      <button
                        type="button"
                        onClick={() => handleEyeClick(url)}
                        className={`shrink-0 transition-colors ${isDarkMode ? 'text-indigo-400 hover:text-indigo-300' : 'text-gray-500 dark:text-gray-400 hover:text-black dark:hover:text-white'}`}
                        aria-label={`View image ${index + 1}`}
                      >
                        <FaEye size={15} />
                      </button>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Action Buttons */}
            <div className="flex gap-2 pt-1">
              <button
                type="button"
                onClick={handleEditToggle}
                className={`text-xs font-medium py-2 px-4 rounded-md transition-all flex items-center gap-1.5 ${
                  isDarkMode
                    ? 'bg-gradient-to-r from-indigo-600 to-purple-600 text-white hover:shadow-[0_0_15px_rgba(99,102,241,0.5)]'
                    : 'bg-black text-white hover:bg-gray-800'
                }`}
              >
                {isEditMode ? (
                  <>
                    <FaTimes size={11} /> Cancel Editing
                  </>
                ) : (
                  <>
                    <FaEdit size={11} /> Edit Details
                  </>
                )}
              </button>

              {isEditMode && (
                <button
                  type="submit"
                  className={`text-xs font-medium py-2 px-4 rounded-md border transition-all flex items-center gap-1.5 ${
                    isDarkMode
                      ? 'border-indigo-500/40 text-indigo-300 hover:bg-indigo-500/10 hover:shadow-[0_0_10px_rgba(99,102,241,0.4)]'
                      : 'border-black text-black hover:bg-black hover:text-white'
                  }`}
                >
                  <FaSave size={11} /> Save Changes
                </button>
              )}
            </div>
          </form>
        </div>
      </DialogContent>
    </Dialog>

  );
};

export default ViewWeightDisputes;